import { useState } from 'react';
import { useSelector } from 'react-redux';

// eslint-disable-next-line import/named
import { runExport } from '@flow/libs/oneflow-client/agreements';
import { getCurrentWorkspaceSelector } from '@app/reducers/app';
import agreementsReducer from '@app/reducers/entities/agreements';
import { getFeatureSelector, getPositionFromSessionSelector } from '@app/reducers/session';
import { SHARED_WITH_ME_ID } from '@app/reducers/entities/workspaces';
import foldersReducer from '@app/reducers/entities/folders';

import { ExportDocumentsModal } from '@app/components/export-button/modals';
import { checkAcl } from '@flow/libs/utils/acl';
import { WORKSPACE_ROOT_FOLDER_ID } from '@flow/libs/workspace/folders-constants';

export type Props = {
  folderId: number;
  workspace: Oneflow.Workspace;
  queryName: string;
};

export const ExportDocuments = ({ folderId, workspace, queryName }: Props) => {
  const [includeSubfolders, setIncludeSubfolders] = useState(true);
  const agreementsQuery = useSelector((state) =>
    agreementsReducer.getQuerySelector(state, {
      name: queryName,
    }),
  );
  const email = useSelector(getPositionFromSessionSelector)?.email;

  const selectedFolder = useSelector((state) =>
    foldersReducer.getFolderSelector(state, {
      id: folderId,
    }),
  ) as Oneflow.Folder;

  const agreementExportFeature = useSelector((state) =>
    getFeatureSelector(state, {
      feature: 'agreementExport',
    }),
  );
  const currentWorkspace = useSelector(getCurrentWorkspaceSelector);
  const isAllowedToExport = checkAcl(currentWorkspace.acl, 'collection:agreement:export');
  const isSharedWithMeWorkspace = currentWorkspace.id === SHARED_WITH_ME_ID;
  const isWithinExportLimit = agreementsQuery.count <= agreementExportFeature.limit;
  const showExportButton =
    Boolean(agreementExportFeature.enabled) &&
    isWithinExportLimit &&
    isAllowedToExport &&
    !isSharedWithMeWorkspace;

  const workspaceId = currentWorkspace.id;

  const onExport = (onSuccess: () => void, onFailure: (code: number) => void) => {
    const includeFolderSubtree = includeSubfolders;
    const folderId = agreementsQuery?.params?.folderIds?.[0];
    let folderIds = agreementsQuery?.params?.folderIds;

    if (folderId === WORKSPACE_ROOT_FOLDER_ID) {
      folderIds = [];
    }

    runExport(
      workspaceId,
      {
        ...agreementsQuery,
        params: {
          ...agreementsQuery.params,
          folderIds,
        },
      },
      includeFolderSubtree,
    )
      .then(onSuccess)
      .catch((error) => {
        onFailure(error.body.status_code);
      });
  };

  const changeCheckboxState = () => {
    setIncludeSubfolders(!includeSubfolders);
  };

  if (!showExportButton) {
    return null;
  }

  const isGlobalSearch = window.location.pathname === '/search/all';

  return (
    <ExportDocumentsModal
      onExport={onExport}
      email={email}
      query={agreementsQuery}
      workspace={isGlobalSearch ? '' : workspace}
      folderId={folderId}
      selectedFolder={selectedFolder}
      includeSubfolders={includeSubfolders}
      changeCheckboxState={changeCheckboxState}
    />
  );
};
